import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  BanknotesIcon,
  ClipboardDocumentCheckIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import Breadcrumb from '../../components/Navigation/Breadcrumb';
import InternalLinks from '../../components/Navigation/InternalLinks';

const PAGE_URL = 'https://smileconsent.co.uk/nhs-mixed-treatment-compliance';

export default function NhsMixedTreatmentCompliance() {
  usePageMeta(
    'Mixed NHS & private dental treatment consent | FP17DC documentation | Smile Consent',
    'Document mixed NHS and private dental treatment with clear cost separation, GDC Standard 2.2 themes, and patient agreement. FP17DC-equivalent consent documentation for UK practices.',
    PAGE_URL
  );

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Breadcrumb
          items={[
            { label: 'NHS mixed treatment compliance', path: '/nhs-mixed-treatment-compliance' },
          ]}
        />
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-4">
        <header className="mb-10">
          <p className="text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
            For NHS dental practices — commissioner guidance on mixed NHS and private treatment
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            Mixed NHS and private dental treatment documentation
          </h1>
          <p className="mt-4 text-lg text-gray-600 leading-relaxed">
            NHS commissioners across England — including the South West — have reminded contractors that patients
            must receive <strong>full, clear information</strong> when NHS and private treatment are combined in
            one course of treatment. Consent must be <strong>free and informed</strong>, with costs that are{' '}
            <strong>clear and unambiguous</strong> before treatment starts.
          </p>
          <p className="mt-3 text-sm text-gray-500">
            This page is general information, not legal advice. For your own situation, take professional legal,
            indemnity, and commissioner guidance.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/book-demo"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-6 py-3 font-semibold text-white hover:bg-emerald-700 transition-colors shadow-sm"
            >
              <CalendarIcon className="h-5 w-5" aria-hidden />
              Book a demo
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 px-6 py-3 font-semibold text-gray-800 hover:bg-gray-50 transition-colors"
            >
              Start free trial
              <ArrowRightIcon className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        </header>

        <div className="space-y-10">
          <section className="rounded-2xl border border-gray-200 bg-slate-50/80 p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <DocumentTextIcon className="h-8 w-8 text-blue-600 shrink-0" aria-hidden />
              <div>
                <h2 className="text-xl font-bold text-gray-900">What commissioners are asking for</h2>
                <p className="mt-3 text-gray-700 leading-relaxed">
                  When both NHS and private options have been discussed and treatment is not available under NHS
                  regulations, clinicians may provide a <strong>mix of NHS and private dental treatment</strong>.
                  The patient must be issued with an <strong>FP17DC form or equivalent</strong> documenting the NHS
                  and private elements planned under the course of treatment (CoT).
                </p>
                <ul className="mt-4 space-y-2 text-gray-700">
                  <li className="flex gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0 mt-0.5" aria-hidden />
                    <span>
                      Keep <strong>clear, accurate, contemporaneous notes</strong> of conversations about mixing NHS
                      and private treatment.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0 mt-0.5" aria-hidden />
                    <span>
                      <strong>Never mislead patients</strong> about what is available on the NHS — a course of
                      treatment includes necessary treatment to secure oral health.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0 mt-0.5" aria-hidden />
                    <span>
                      Discuss <strong>NHS and private options</strong> with outcomes, differences, and costs
                      explained clearly. Patients are <strong>not obliged</strong> to accept private treatment.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0 mt-0.5" aria-hidden />
                    <span>
                      Provide a <strong>treatment plan</strong> the patient has agreed to — signed, and preferably
                      documented on FP17DC or equivalent.
                    </span>
                  </li>
                </ul>
                <p className="mt-4 text-sm text-gray-600">
                  Smile Consent supports patient-facing consent and cost documentation aligned with these principles.
                  It does <strong>not</strong> replace official BSA Compass FP17DC submission processes.
                </p>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-amber-200 bg-amber-50/60 p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-8 w-8 text-amber-600 shrink-0" aria-hidden />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Orthodontic treatment</h2>
                <p className="mt-3 text-gray-700 leading-relaxed">
                  Commissioner guidance is explicit: <strong>orthodontic provision must be entirely private or
                  entirely NHS</strong> — not mixed within the same plan. Practices should treat orthodontics as a
                  separate pathway when documenting consent and costs.
                </p>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <ShieldCheckIcon className="h-8 w-8 text-emerald-600 shrink-0" aria-hidden />
              <div>
                <h2 className="text-xl font-bold text-gray-900">GDC Standard 2.2 — communicating with patients</h2>
                <p className="mt-3 text-gray-700 leading-relaxed">
                  The <strong>General Dental Council</strong> expects registrants to recognise and promote patients&apos;
                  rights to make decisions about their care. Before treatment starts you must:
                </p>
                <ul className="mt-4 space-y-2 text-gray-700">
                  <li className="flex gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0 mt-0.5" aria-hidden />
                    <span>
                      Explain the <strong>options</strong> — including delaying treatment or doing nothing — with the
                      risks and benefits of each.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0 mt-0.5" aria-hidden />
                    <span>
                      Give <strong>full information</strong> on the treatment you propose and the{' '}
                      <strong>possible costs</strong>.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0 mt-0.5" aria-hidden />
                    <span>
                      Encourage patients to ask questions and give <strong>full and honest answers</strong> about
                      their options or treatment.
                    </span>
                  </li>
                </ul>
                <p className="mt-4 text-sm text-gray-600">
                  Reference:{' '}
                  <a
                    href="https://standards.gdc-uk.org/pages/principle2/principle2"
                    className="text-blue-600 underline hover:text-blue-800"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    GDC Standards — Principle 2.2
                  </a>
                </p>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <BanknotesIcon className="h-8 w-8 text-indigo-600 shrink-0" aria-hidden />
              <div>
                <h2 className="text-xl font-bold text-gray-900">FP17DC and equivalent documentation</h2>
                <p className="mt-3 text-gray-700 leading-relaxed">
                  The <strong>FP17DC</strong> records which elements of a planned course of treatment are NHS and
                  which are private, together with the patient&apos;s agreement. Commissioners accept{' '}
                  <strong>equivalent documentation</strong> that clearly separates NHS band charges from private item
                  fees and shows the patient has agreed before treatment.
                </p>
                <p className="mt-3 text-gray-700 leading-relaxed">
                  For mixed plans, documentation should show the <strong>NHS band</strong> (one charge covering NHS
                  elements in that CoT), each <strong>private item and fee</strong>, and a <strong>total</strong> that
                  is clear and unambiguous — not a single blended figure with no breakdown.
                </p>
              </div>
            </div>
          </section>

          <section>
            <div className="flex items-start gap-3 mb-4">
              <ClipboardDocumentCheckIcon className="h-8 w-8 text-emerald-600 shrink-0" aria-hidden />
              <h2 className="text-xl font-bold text-gray-900">How Smile Consent helps today</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              Smile Consent is built for UK dental practices documenting{' '}
              <strong>mixed NHS private dental treatment consent</strong>. Clinicians review and approve every
              draft before it is sent — the platform supports your workflow; it does not replace clinical judgment.
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0 mt-0.5" aria-hidden />
                <span>
                  <strong>Mixed NHS + Private</strong> payment type with NHS band selection and per-item private fees.
                </span>
              </li>
              <li className="flex gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0 mt-0.5" aria-hidden />
                <span>
                  <strong>Cost section</strong> that separates NHS band charge from private line items and shows a
                  combined total — aligned with FP17DC documentation principles.
                </span>
              </li>
              <li className="flex gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0 mt-0.5" aria-hidden />
                <span>
                  AI-assisted consent wording covering <strong>risks, alternatives, and options</strong> — edited and
                  signed off by the treating clinician before send.
                </span>
              </li>
              <li className="flex gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0 mt-0.5" aria-hidden />
                <span>
                  <strong>Digital patient signing</strong> with timestamped records (sent, opened, signed) for audit
                  and complaint defence.
                </span>
              </li>
              <li className="flex gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0 mt-0.5" aria-hidden />
                <span>
                  <strong>PDF archive</strong> of signed consent for your records alongside clinical notes in your
                  practice software.
                </span>
              </li>
            </ul>
          </section>
        </div>

        <div className="mt-14 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 text-white p-8 sm:p-10">
          <h2 className="text-xl sm:text-2xl font-bold">See mixed treatment documentation in action</h2>
          <p className="mt-2 text-emerald-100 leading-relaxed">
            Book a short demo and we&apos;ll walk through mixed NHS and private consent — cost breakdown, patient
            signing, and the records your team keeps.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/book-demo"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors"
            >
              <CalendarIcon className="h-5 w-5" aria-hidden />
              Book a demo
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/80 px-6 py-3 font-semibold text-white hover:bg-white/10 transition-colors"
            >
              Start free trial
              <ArrowRightIcon className="h-4 w-4" aria-hidden />
            </Link>
          </div>
          <p className="mt-6 text-sm text-emerald-200">
            Related reading:{' '}
            <Link to="/dental-consent-law-uk" className="underline hover:text-white">
              dental consent law in the UK
            </Link>
            {' · '}
            <Link to="/dental-compliance-software" className="underline hover:text-white">
              compliance software for practices
            </Link>
            {' · '}
            <Link to="/cpd-legal-consent" className="underline hover:text-white">
              free CPD on legal consent
            </Link>
            .
          </p>
        </div>

        <div className="mt-12">
          <InternalLinks
            title="Related resources"
            links={[
              {
                title: 'Dental consent law UK',
                description: 'Montgomery, GDC standards, CQC Regulation 11, and Mental Capacity Act themes',
                path: '/dental-consent-law-uk',
              },
              {
                title: 'Dental compliance software',
                description: 'Digital consent and documentation for UK practice compliance',
                path: '/dental-compliance-software',
              },
              {
                title: 'NHS dental contract calculators',
                description: 'UDA and urgent CoT planning tools for NHS contractors',
                path: '/nhs-dental-contract-calculators',
              },
              {
                title: 'Orthodontic consent form UK',
                description: 'Separate NHS or private orthodontic consent — not mixed plans',
                path: '/orthodontic-consent-form-uk',
              },
            ]}
          />
        </div>
      </article>
    </div>
  );
}
